import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import SEOHead from '../../components/base/SEOHead';
import { supabase } from '../../lib/supabase';
import GameArena from './components/GameArena';
import GameStats from './components/GameStats';
import GameHistory from './components/GameHistory';
import RewardSystem from './components/RewardSystem';

interface User {
  id: string;
  email: string;
}

interface GameStats {
  id?: string;
  user_id?: string;
  total_games?: number;
  games_won?: number;
  games_lost?: number;
  total_earnings?: number;
  total_spent?: number;
  win_streak?: number;
  best_streak?: number;
  level?: number;
  experience?: number;
  achievements?: string[];
  created_at?: string;
  updated_at?: string;
}

export default function GamePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [gameStats, setGameStats] = useState<GameStats>({
    total_games: 0,
    games_won: 0,
    games_lost: 0,
    total_earnings: 0,
    total_spent: 0,
    win_streak: 0,
    best_streak: 0,
    level: 1,
    experience: 0,
    achievements: []
  });
  const [activeTab, setActiveTab] = useState<'play' | 'stats' | 'history' | 'rewards'>('play');
  const [showTutorial, setShowTutorial] = useState(false);
  const [balance, setBalance] = useState(0);

  // Add refresh functionality
  const refreshPage = () => {
    // Reset all game states
    setGameStats({
      total_games: 0,
      games_won: 0,
      games_lost: 0,
      total_earnings: 0,
      total_spent: 0,
      win_streak: 0,
      best_streak: 0,
      level: 1,
      experience: 0,
      achievements: []
    });
    
    // Reload user data
    if (user) {
      loadGameStats(user.id);
    }
    
    // Reset active tab to game arena
    setActiveTab('play');
    
    // Clear any connection errors
    setConnectionError(null);
    
    // Show success message
    const refreshMessage = document.createElement('div');
    refreshMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
    refreshMessage.textContent = 'Game data refreshed successfully!';
    document.body.appendChild(refreshMessage);
    
    setTimeout(() => {
      if (document.body.contains(refreshMessage)) {
        document.body.removeChild(refreshMessage);
      }
    }, 3000);
  };

  useEffect(() => {
    checkAuth();
    
    // Failsafe: Show content after 2 seconds max
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('⏱️ Loading timeout - showing content');
        setLoading(false);
        if (!user) {
          setUser({ id: 'guest', email: 'guest@local' });
          setOfflineMode(true);
        }
      }
    }, 2000);
    
    return () => clearTimeout(timeout);
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        // Allow guest play
        console.log('🎮 Starting guest session');
        setUser({ id: 'guest', email: 'guest@local' });
        setOfflineMode(true);
        setLoading(false);
        return;
      }

      setUser(user);
      await loadGameData(user.id);
    } catch (error) {
      console.error('Auth check error:', error);
      // Fallback to guest mode on any error
      setUser({ id: 'guest', email: 'guest@local' });
      setOfflineMode(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_wallets')
        .select('pepcoin_balance')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setBalance(data.pepcoin_balance || 0);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGameData = async (userId: string) => {
    try {
      // Load game stats
      await loadGameStats(userId);
      
      // Load wallet balance
      const { data: walletData } = await supabase
        .from('user_wallets')
        .select('pepcoin_balance')
        .eq('user_id', userId)
        .single();
      
      if (walletData) {
        setBalance(walletData.pepcoin_balance || 0);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading game data:', error);
      setLoading(false);
    }
  };

  const loadGameStats = async (userId: string) => {
    try {
      if (userId === 'guest' || offlineMode) {
        // Use local storage for guest/offline mode
        const localStats = localStorage.getItem('pepCoinGameStats');
        if (localStats) {
          setGameStats(JSON.parse(localStats));
        }
        setOfflineMode(true);
        return;
      }

      // Try to load from database with fallback
      const { data: statsData, error: statsError } = await supabase
        .from('game_stats')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (statsError) {
        if (statsError.code === 'PGRST205') {
          // Table doesn't exist, use default stats
          console.log('Game stats table not found, using default stats');
          const defaultStats = {
            id: '',
            user_id: userId,
            total_games: 0,
            games_won: 0,
            games_lost: 0,
            total_earnings: 0,
            total_spent: 0,
            win_streak: 0,
            best_streak: 0,
            level: 1,
            experience: 0,
            achievements: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setGameStats(defaultStats);
        } else if (statsError.code === 'PGRST116') {
          // No stats found for user, create default
          const defaultStats = {
            user_id: userId,
            total_games: 0,
            games_won: 0,
            games_lost: 0,
            total_earnings: 0,
            total_spent: 0,
            win_streak: 0,
            best_streak: 0,
            level: 1,
            experience: 0,
            achievements: []
          };

          // Try to insert default stats
          const { data: newStats, error: insertError } = await supabase
            .from('game_stats')
            .insert(defaultStats)
            .select()
            .single();

          if (!insertError && newStats) {
            setGameStats(newStats);
          } else {
            setGameStats({
              id: '',
              ...defaultStats,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          }
        } else {
          console.error('Stats error:', statsError);
          setConnectionError('Database connection failed. Using offline mode.');
          setOfflineMode(true);
          // Use default stats as fallback
          setGameStats({
            id: '',
            user_id: userId,
            total_games: 0,
            games_won: 0,
            games_lost: 0,
            total_earnings: 0,
            total_spent: 0,
            win_streak: 0,
            best_streak: 0,
            level: 1,
            experience: 0,
            achievements: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }
      } else if (statsData) {
        setGameStats(statsData);
      }
    } catch (error) {
      console.error('Load stats error:', error);
      setConnectionError('Failed to load game data. Using offline mode.');
      setOfflineMode(true);
      // Use default stats as fallback
      setGameStats({
        id: '',
        user_id: userId,
        total_games: 0,
        games_won: 0,
        games_lost: 0,
        total_earnings: 0,
        total_spent: 0,
        win_streak: 0,
        best_streak: 0,
        level: 1,
        experience: 0,
        achievements: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    }
  };

  const updateGameStats = async (newStats: Partial<GameStats>) => {
    const updatedStats = { ...gameStats, ...newStats };
    setGameStats(updatedStats);
    
    // Save to local storage for offline mode
    if (offlineMode || user?.id === 'guest') {
      localStorage.setItem('pepCoinGameStats', JSON.stringify(updatedStats));
      return;
    }
    
    // Try to save to database
    try {
      if (user && user.id !== 'guest') {
        await supabase
          .from('game_stats')
          .upsert({
            user_id: user.id,
            ...updatedStats,
            updated_at: new Date().toISOString()
          });
      }
    } catch (error) {
      console.warn('Failed to save stats to database, saved locally:', error);
      localStorage.setItem('pepCoinGameStats', JSON.stringify(updatedStats));
    }
  };

  const scrollToGameArena = () => {
    const gameArena = document.querySelector('.game-arena-section');
    if (gameArena) {
      (gameArena as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-purple-500/30 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="w-20 h-20 border-4 border-t-purple-500 rounded-full animate-spin absolute top-0 left-1/2 transform -translate-x-1/2"></div>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Loading PepCoin Arena</h3>
          <p className="text-purple-300 mb-6">Preparing your gaming experience...</p>
          
          <button
            onClick={refreshPage}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-xl"></i>
          </button>
        </div>
      </div>
    );
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Game",
    "name": "PepCoin Web3 Gaming Arena",
    "description": "Blockchain-powered play-to-earn gaming with NFT rewards, smart contract gameplay, and decentralized prize pools.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://pepcoin.com"}/game`,
    "genre": "Web3 Strategy",
    "gamePlatform": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEOHead 
        title="PepCoin Web3 Gaming Arena - Play-to-Earn Blockchain Rewards"
        description="Play blockchain-powered games and earn crypto rewards. NFT prizes, smart contract gameplay, and decentralized prize pools in the PepCoin Web3 ecosystem."
        keywords="Web3 gaming, play-to-earn, blockchain games, NFT rewards, crypto gaming, DeFi gaming, PepCoin arena, smart contract games"
        schemaData={schemaData}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <Navbar />
        
        {/* Connection Status Banner */}
        {(offlineMode || connectionError) && (
          <div className="bg-yellow-500/20 border-b border-yellow-500/30 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <i className="ri-wifi-off-line text-yellow-400 text-xl"></i>
                  <span className="text-yellow-100 font-medium">
                    {connectionError || 'Playing in offline mode - progress saved locally'}
                  </span>
                </div>
                <button
                  onClick={refreshPage}
                  className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-100 px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-refresh-line text-lg"></i>
                  <span className="hidden sm:inline">Retry</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Back Button */}
        <div className="pt-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-black/30 hover:bg-black/40 border border-purple-500/30 rounded-lg text-white transition-all duration-300 cursor-pointer group mb-4"
            >
              <i className="ri-arrow-left-line text-lg group-hover:-translate-x-1 transition-transform"></i>
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative pb-16 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              {/* Main Title */}
              <div className="mb-6">
                <h1 className="text-5xl md:text-7xl font-black mb-4">
                  <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                    Web3
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                    Gaming Arena
                  </span>
                </h1>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent flex-1 max-w-32"></div>
                  <div className="text-2xl">⛓️</div>
                  <div className="h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent flex-1 max-w-32"></div>
                </div>
              </div>
              
              {/* Subtitle */}
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8 leading-relaxed">
                Experience the future of 
                <span className="text-purple-400 font-semibold"> play-to-earn blockchain gaming</span>. 
                Earn NFT rewards, stake tokens, and compete in 
                <span className="text-green-400 font-semibold"> decentralized prize pools!</span>
              </p>
              
              {/* Feature Highlights */}
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                <div className="bg-black/30 backdrop-blur-sm border border-purple-500/30 rounded-full px-6 py-3 flex items-center gap-2">
                  <i className="ri-links-fill text-purple-400"></i>
                  <span className="text-white font-medium">On-Chain</span>
                </div>
                <div className="bg-black/30 backdrop-blur-sm border border-green-500/30 rounded-full px-6 py-3 flex items-center gap-2">
                  <i className="ri-nft-fill text-green-400"></i>
                  <span className="text-white font-medium">NFT Rewards</span>
                </div>
                <div className="bg-black/30 backdrop-blur-sm border border-blue-500/30 rounded-full px-6 py-3 flex items-center gap-2">
                  <i className="ri-shield-check-line text-blue-400"></i>
                  <span className="text-white font-medium">Smart Contracts</span>
                </div>
                <div className="bg-black/30 backdrop-blur-sm border border-yellow-500/30 rounded-full px-6 py-3 flex items-center gap-2">
                  <i className="ri-coin-line text-yellow-400"></i>
                  <span className="text-white font-medium">
                    {offlineMode ? 'Offline Mode' : 'DeFi Staking'}
                  </span>
                </div>
              </div>
              
              {/* Quick Stats Dashboard */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-black text-purple-400 mb-2">
                    {(gameStats?.total_games || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-300 font-medium">Games Played</div>
                  <div className="text-xs text-purple-300 mt-1">On-Chain Sessions</div>
                </div>
                
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm border border-green-500/30 rounded-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-black text-green-400 mb-2">
                    {(gameStats?.total_earnings || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-300 font-medium">Tokens Earned</div>
                  <div className="text-xs text-green-300 mt-1">Crypto Rewards</div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-500/30 rounded-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-black text-blue-400 mb-2">
                    {(gameStats?.win_streak || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-300 font-medium">Win Streak</div>
                  <div className="text-xs text-blue-300 mt-1">NFT Multiplier</div>
                </div>
                
                <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-2xl p-6 transform hover:scale-105 transition-all duration-300">
                  <div className="text-3xl md:text-4xl font-black text-yellow-400 mb-2">
                    {Math.round(((gameStats?.games_won || 0) / Math.max((gameStats?.total_games || 0), 1)) * 100)}%
                  </div>
                  <div className="text-sm text-gray-300 font-medium">Win Rate</div>
                  <div className="text-xs text-yellow-300 mt-1">Blockchain Verified</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-black/20 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-2 mb-8">
            <div className="flex flex-wrap justify-center gap-2">
              {[
                { id: 'play', label: 'Play & Earn', icon: 'ri-gamepad-line', color: 'from-purple-500 to-pink-500' },
                { id: 'stats', label: 'On-Chain Stats', icon: 'ri-bar-chart-line', color: 'from-blue-500 to-cyan-500' },
                { id: 'history', label: 'TX History', icon: 'ri-history-line', color: 'from-green-500 to-emerald-500' },
                { id: 'rewards', label: 'NFT Rewards', icon: 'ri-gift-line', color: 'from-yellow-500 to-orange-500' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-6 py-4 rounded-xl font-bold transition-all duration-300 flex items-center gap-3 whitespace-nowrap cursor-pointer transform hover:scale-105 ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.color} text-white shadow-lg shadow-purple-500/25`
                      : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-white'
                  }`}
                >
                  <i className={`${tab.icon} text-xl`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[600px] bg-black/10 backdrop-blur-sm border border-gray-700/30 rounded-2xl p-6">
            {activeTab === 'play' && (
              <GameArena 
                user={user} 
                gameStats={gameStats} 
                onStatsUpdate={updateGameStats}
                offlineMode={offlineMode}
              />
            )}
            {activeTab === 'stats' && (
              <GameStats 
                user={user} 
                gameStats={gameStats}
                offlineMode={offlineMode}
              />
            )}
            {activeTab === 'history' && (
              <GameHistory 
                user={user}
                offlineMode={offlineMode}
              />
            )}
            {activeTab === 'rewards' && (
              <RewardSystem 
                user={user} 
                gameStats={gameStats}
                offlineMode={offlineMode}
              />
            )}
          </div>
        </div>

        {/* Game Features Section */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Web3 Gaming Features
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience blockchain-powered gaming with true ownership and decentralized rewards
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-gradient-to-br from-purple-900/40 to-pink-900/40 backdrop-blur-sm border border-purple-500/30 rounded-3xl p-8 text-center transform hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/25">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:rotate-12 transition-transform duration-300">
                <i className="ri-links-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Smart Contract Gaming</h3>
              <p className="text-gray-300 leading-relaxed">
                All game outcomes are verified on-chain through smart contracts, ensuring provably fair gameplay and transparent reward distribution.
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-sm">Verifiable</span>
                <span className="bg-pink-500/20 text-pink-300 px-3 py-1 rounded-full text-sm">Trustless</span>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-green-900/40 to-emerald-900/40 backdrop-blur-sm border border-green-500/30 rounded-3xl p-8 text-center transform hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-green-500/25">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:rotate-12 transition-transform duration-300">
                <i className="ri-nft-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">NFT Rewards System</h3>
              <p className="text-gray-300 leading-relaxed">
                Earn unique NFT rewards for achievements and milestones. Trade, stake, or use them to unlock exclusive game features and DeFi benefits.
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm">Tradeable</span>
                <span className="bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full text-sm">
                  {offlineMode ? 'Local Save' : 'Stakeable'}
                </span>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-blue-900/40 to-cyan-900/40 backdrop-blur-sm border border-blue-500/30 rounded-3xl p-8 text-center transform hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/25">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:rotate-12 transition-transform duration-300">
                <i className="ri-safe-2-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">
                {offlineMode ? 'Offline Gaming' : 'DeFi Prize Pools'}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {offlineMode 
                  ? 'Play anytime, anywhere! Your progress is saved locally and will sync to the blockchain when connection is restored.'
                  : 'Compete for decentralized prize pools funded by staking rewards. Higher stakes mean bigger rewards in our yield-generating gaming ecosystem.'
                }
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full text-sm">
                  {offlineMode ? 'Offline' : 'Yield'}
                </span>
                <span className="bg-cyan-500/20 text-cyan-300 px-3 py-1 rounded-full text-sm">
                  {offlineMode ? 'Local Save' : 'Decentralized'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-sm border border-purple-500/30 rounded-3xl p-12 text-center">
            <h3 className="text-3xl md:text-4xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Ready to Earn Crypto?
              </span>
            </h3>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {offlineMode 
                ? 'Start playing now in offline mode! Your progress will be saved locally and synced to the blockchain when connection is restored.'
                : 'Join thousands of players earning PepCoins and NFTs daily. Connect your wallet and start your Web3 gaming journey now!'
              }
            </p>
            <button
              onClick={scrollToGameArena}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-12 py-4 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-2xl mr-3"></i>
              Start Playing Now
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
